/**
 * @fileoverview Song List Item Component
 *
 * Individual song card displayed in the search results list.
 * Shows song title, verb info, and indicates if audio is available.
 *
 * @module components/SongListItem
 */

import React from 'react'
import type { Doc } from '../../convex/_generated/dataModel'
import { Music, Volume2 } from 'lucide-react'

interface SongListItemProps {
  song: Doc<'songs'> & { audioGeneration?: Doc<'temporGenerations'> | null }
  isSelected?: boolean
  onClick: () => void
  searchHighlight?: string
}

/**
 * Highlight matching search terms in text
 */
function highlightText(text: string, highlight?: string): React.ReactNode {
  if (!highlight || !text) {
    return text
  }

  const parts = text.split(new RegExp(`(${highlight})`, 'gi'))
  return parts.map((part, index) =>
    part.toLowerCase() === highlight.toLowerCase() ? (
      <mark key={index} className="bg-yellow-200 text-gray-900 px-0.5 rounded">
        {part}
      </mark>
    ) : (
      part
    )
  )
}

export function SongListItem({ song, isSelected, onClick, searchHighlight }: SongListItemProps) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left px-4 py-3 rounded-lg border transition-all
        focus:outline-none focus:ring-2 focus:ring-cyan-500
        ${
          isSelected
            ? 'bg-cyan-50 border-cyan-500 shadow-sm'
            : 'bg-white border-gray-200 hover:border-cyan-300 hover:shadow'
        }
      `}
      aria-pressed={isSelected}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={`
          flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
          ${isSelected ? 'bg-cyan-600' : 'bg-gray-100'}
        `}
        >
          <Music className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-gray-600'}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className="font-medium text-gray-900 truncate">
            {highlightText(song.title, searchHighlight)}
          </h3>

          {/* Verb info */}
          <p className="text-sm text-gray-600 mt-0.5">
            <span className="font-medium">{highlightText(song.targetVerb, searchHighlight)}</span>
            {' • '}
            <span className="text-gray-500">{highlightText(song.sourceVerb, searchHighlight)}</span>
          </p>

          {/* Metadata */}
          <div className="flex items-center gap-2 mt-1.5">
            {song.audioGeneration && (
              <span className="inline-flex items-center gap-1 text-xs text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded-full">
                <Volume2 className="w-3 h-3" />
                Audio
              </span>
            )}
            {song.difficulty && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {song.difficulty}
              </span>
            )}
            <span className="text-xs text-gray-400">{song.sections.length} sections</span>
          </div>
        </div>
      </div>
    </button>
  )
}
